import type { BadgeTone } from '@/components/ui/Badge';

/**
 * Klartext-Ursachen für fehlgeschlagene Steps/Runs.
 *
 * Anlass (11.08.2026): "alles warten" endete mit neun roten Zeilen
 * "fehlgeschlagen". Keine einzige Routine war gescheitert - sie wurden nie
 * erreicht (Yubikey nicht berührt, kein Schlüssel für den Host, bei der
 * OPNsense ist SSH gar nicht offen). Neun identische rote Zeilen lasen sich
 * als "das Tool ist kaputt".
 *
 * Die Klassifikation macht das Backend
 * (app/services/run_engine/failure_reason.py) und berechnet sie beim
 * Ausliefern, damit auch alte Läufe rückwirkend lesbar sind. Hier stehen nur
 * die passenden Typen und die Darstellungsregeln.
 *
 * Die Typen sind bewusst hier statt in hooks/useRuns.ts definiert: die Runs-
 * Hooks werden parallel angefasst, und diese Felder hängen an genau zwei
 * Views (Run-Liste, Run-Detail).
 */

/** Ursache eines einzelnen fehlgeschlagenen Steps. */
export interface StepFailureReason {
  /** Stabiler Schlüssel, z.B. 'ssh_key_not_confirmed'. */
  category: string;
  /** Klartext-Ursache, eine Zeile. */
  title: string;
  /** Was jetzt zu tun ist. */
  action: string;
  /** Kurzform für Badges (max. ~22 Zeichen). */
  short_label: string;
  /** 'verbindung' | 'konfiguration' | 'zielsystem' | 'wartung' | 'unklar'. */
  group: string;
  /** true = am Zielsystem wurde nichts verändert, Lauf ist wiederholbar. */
  retryable: boolean;
  /** Belegende Zeile aus der Rohausgabe; null = nichts Eindeutiges gefunden. */
  evidence?: string | null;
}

/** Dominante Ursache eines ganzen Runs (Run-History-Liste). */
export interface RunFailureSummary {
  category: string;
  title: string;
  action: string;
  short_label: string;
  group: string;
  retryable: boolean;
  /** Steps mit genau dieser Kategorie. */
  steps_affected: number;
  /** Fehlgeschlagene Steps insgesamt. */
  steps_failed: number;
}

/** Run-Typ + Fehler-Zusammenfassung (Backend liefert das Feld immer mit). */
export type WithRunFailure<T> = T & { failure?: RunFailureSummary | null };

/** Step-Typ + Ursache und stderr-Ausschnitt. */
export type WithStepFailure<T> = T & {
  stderr_excerpt?: string | null;
  failure_reason?: StepFailureReason | null;
};

/**
 * Farbe nach Gruppe - der ganze Punkt dieser Übung.
 *
 * Gelb heißt "wir sind gar nicht drangekommen" (Zugang/Netz/Konfiguration),
 * rot heißt "die Wartung selbst ist gescheitert". Das sind zwei völlig
 * verschiedene Dinge und müssen sich auf einen Blick unterscheiden.
 */
export function failureTone(group: string | undefined): BadgeTone {
  switch (group) {
    case 'verbindung':
    case 'konfiguration':
    case 'zielsystem':
      return 'warning';
    case 'wartung':
      return 'danger';
    default:
      return 'danger';
  }
}

/** Überschrift der Gruppe - beantwortet "ist am System etwas passiert?". */
export function failureGroupLabel(group: string | undefined): string {
  switch (group) {
    case 'verbindung':
      return 'Nicht erreicht - am System wurde nichts verändert';
    case 'konfiguration':
      return 'Nicht gestartet - im Tool fehlt etwas';
    case 'zielsystem':
      return 'Zielsystem erreicht, aber nicht wartbar';
    case 'wartung':
      return 'Wartung wurde ausgeführt und ist gescheitert';
    default:
      return 'Ursache unklar';
  }
}

/** Randfarbe für den prominenten Kasten (Tailwind-Klassen des Themes). */
export function failureBoxClasses(group: string | undefined): string {
  return failureTone(group) === 'danger'
    ? 'border-danger/30 bg-danger/5'
    : 'border-warning/30 bg-warning/5';
}

/** Textfarbe für die Ursachen-Überschrift. */
export function failureTextClass(group: string | undefined): string {
  return failureTone(group) === 'danger' ? 'text-danger' : 'text-warning';
}

/**
 * Rohausgabe eines Steps zum Aufklappen: stderr, stdout, error_message -
 * beschriftet und in dieser Reihenfolge, weil die SSH-Meldung im stderr steht.
 */
export function rohausgabe(step: {
  stdout_excerpt?: string | null;
  stderr_excerpt?: string | null;
  error_message?: string | null;
}): string {
  const teile: string[] = [];
  if (step.stderr_excerpt) teile.push(`--- stderr ---\n${step.stderr_excerpt}`);
  if (step.stdout_excerpt) teile.push(`--- stdout ---\n${step.stdout_excerpt}`);
  if (step.error_message) teile.push(`--- Meldung ---\n${step.error_message}`);
  return teile.join('\n\n');
}
